import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const UserFormModal = ({ isOpen, onClose, onSubmit, user, language, direction }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    role: '',
    status: 'active',
    interfaceLanguage: 'fr',
    permissions: []
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (user) {
      setFormData({
        name: user?.name || '',
        email: user?.email || '',
        phone: user?.phone || '',
        role: user?.role || '',
        status: user?.status || 'active',
        interfaceLanguage: user?.interfaceLanguage || 'fr',
        permissions: user?.permissions || []
      });
    } else {
      setFormData({
        name: '',
        email: '',
        phone: '',
        role: '',
        status: 'active',
        interfaceLanguage: 'fr',
        permissions: []
      });
    }
    setErrors({});
  }, [user, isOpen]);

  const labels = {
    title: {
      en: user ? 'Edit User' : 'Add New User',
      fr: user ? 'Modifier l\'utilisateur' : 'Ajouter un nouvel utilisateur',
      ar: user ? 'تعديل المستخدم' : 'إضافة مستخدم جديد'
    },
    name: {
      en: 'Full Name',
      fr: 'Nom complet',
      ar: 'الاسم الكامل'
    },
    email: {
      en: 'Email Address',
      fr: 'Adresse e-mail',
      ar: 'عنوان البريد الإلكتروني'
    },
    phone: {
      en: 'Phone Number',
      fr: 'Numéro de téléphone',
      ar: 'رقم الهاتف'
    },
    role: {
      en: 'Role',
      fr: 'Rôle',
      ar: 'الدور'
    },
    status: {
      en: 'Status',
      fr: 'Statut',
      ar: 'الحالة'
    },
    interfaceLanguage: {
      en: 'Interface Language',
      fr: 'Langue de l\'interface',
      ar: 'لغة الواجهة'
    },
    permissions: {
      en: 'Permissions',
      fr: 'Autorisations',
      ar: 'الصلاحيات'
    },
    cancel: {
      en: 'Cancel',
      fr: 'Annuler',
      ar: 'إلغاء'
    },
    save: {
      en: user ? 'Update User' : 'Create User',
      fr: user ? 'Mettre à jour' : 'Créer l\'utilisateur',
      ar: user ? 'تحديث المستخدم' : 'إنشاء مستخدم'
    }
  };

  const roleOptions = [
    { value: 'administrator', label: language === 'en' ? 'Administrator' : language === 'fr' ? 'Administrateur' : 'مدير' },
    { value: 'lawyer', label: language === 'en' ? 'Lawyer' : language === 'fr' ? 'Avocat' : 'محامي' },
    { value: 'secretary', label: language === 'en' ? 'Secretary' : language === 'fr' ? 'Secrétaire' : 'سكرتير' }
  ];

  const statusOptions = [
    { value: 'active', label: language === 'en' ? 'Active' : language === 'fr' ? 'Actif' : 'نشط' },
    { value: 'inactive', label: language === 'en' ? 'Inactive' : language === 'fr' ? 'Inactif' : 'غير نشط' },
    { value: 'suspended', label: language === 'en' ? 'Suspended' : language === 'fr' ? 'Suspendu' : 'معلق' }
  ];

  const languageOptions = [
    { value: 'en', label: 'English' },
    { value: 'fr', label: 'Français' },
    { value: 'ar', label: 'العربية' }
  ];

  const permissionsList = [
    { id: 'client_view', labelEn: 'View Clients', labelFr: 'Voir les clients', labelAr: 'عرض العملاء' },
    { id: 'client_edit', labelEn: 'Edit Clients', labelFr: 'Modifier les clients', labelAr: 'تعديل العملاء' },
    { id: 'case_view', labelEn: 'View Cases', labelFr: 'Voir les dossiers', labelAr: 'عرض القضايا' },
    { id: 'case_edit', labelEn: 'Edit Cases', labelFr: 'Modifier les dossiers', labelAr: 'تعديل القضايا' },
    { id: 'billing_view', labelEn: 'View Billing', labelFr: 'Voir la facturation', labelAr: 'عرض الفواتير' },
    { id: 'billing_edit', labelEn: 'Edit Billing', labelFr: 'Modifier la facturation', labelAr: 'تعديل الفواتير' },
    { id: 'document_view', labelEn: 'View Documents', labelFr: 'Voir les documents', labelAr: 'عرض المستندات' },
    { id: 'document_edit', labelEn: 'Edit Documents', labelFr: 'Modifier les documents', labelAr: 'تعديل المستندات' },
    { id: 'admin_access', labelEn: 'Admin Access', labelFr: 'Accès administrateur', labelAr: 'وصول المسؤول' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handlePermissionToggle = (permissionId) => {
    setFormData(prev => ({
      ...prev,
      permissions: prev?.permissions?.includes(permissionId)
        ? prev?.permissions?.filter(p => p !== permissionId)
        : [...prev?.permissions, permissionId]
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData?.name?.trim()) {
      newErrors.name = language === 'en' ? 'Name is required' : language === 'fr' ? 'Le nom est requis' : 'الاسم مطلوب';
    }
    
    if (!formData?.email?.trim()) {
      newErrors.email = language === 'en' ? 'Email is required' : language === 'fr' ? 'L\'e-mail est requis' : 'البريد الإلكتروني مطلوب';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
      newErrors.email = language === 'en' ? 'Invalid email format' : language === 'fr' ? 'Format d\'e-mail invalide' : 'تنسيق البريد الإلكتروني غير صالح';
    }
    
    if (!formData?.role) {
      newErrors.role = language === 'en' ? 'Role is required' : language === 'fr' ? 'Le rôle est requis' : 'الدور مطلوب';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-[200]" onClick={onClose} />
      <div className="fixed inset-0 z-[201] flex items-center justify-center p-4">
        <div 
          className="bg-card border border-border rounded-lg shadow-elevation-lg w-full max-w-2xl max-h-[90vh] overflow-hidden"
          style={{ direction }}
        >
          <div className="flex items-center justify-between px-6 py-4 border-b border-border">
            <h2 className="text-xl font-semibold text-foreground">{labels?.title?.[language]}</h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-muted rounded-lg transition-colors"
              aria-label="Close"
            >
              <Icon name="X" size={20} />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="overflow-y-auto max-h-[calc(90vh-140px)]">
            <div className="p-6 space-y-4">
              <Input
                label={labels?.name?.[language]}
                type="text"
                placeholder={language === 'en' ? 'Enter full name' : language === 'fr' ? 'Entrez le nom complet' : 'أدخل الاسم الكامل'}
                value={formData?.name}
                onChange={(e) => handleInputChange('name', e?.target?.value)}
                error={errors?.name}
                required
              />

              <Input
                label={labels?.email?.[language]}
                type="email"
                placeholder={language === 'en' ? 'Enter email address' : language === 'fr' ? 'Entrez l\'adresse e-mail' : 'أدخل عنوان البريد الإلكتروني'}
                value={formData?.email}
                onChange={(e) => handleInputChange('email', e?.target?.value)}
                error={errors?.email}
                required
              />

              <Input
                label={labels?.phone?.[language]}
                type="tel"
                placeholder="+212 6XX XXX XXX"
                value={formData?.phone}
                onChange={(e) => handleInputChange('phone', e?.target?.value)}
              />

              <Select
                label={labels?.role?.[language]}
                options={roleOptions}
                value={formData?.role}
                onChange={(value) => handleInputChange('role', value)}
                error={errors?.role}
                required
              />

              <Select
                label={labels?.status?.[language]}
                options={statusOptions}
                value={formData?.status}
                onChange={(value) => handleInputChange('status', value)}
              />

              <Select
                label={labels?.interfaceLanguage?.[language]}
                options={languageOptions}
                value={formData?.interfaceLanguage}
                onChange={(value) => handleInputChange('interfaceLanguage', value)}
              />

              <div className="space-y-3">
                <label className="text-sm font-medium text-foreground">
                  {labels?.permissions?.[language]}
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {permissionsList?.map((permission) => (
                    <Checkbox
                      key={permission?.id}
                      label={language === 'en' ? permission?.labelEn : language === 'fr' ? permission?.labelFr : permission?.labelAr}
                      checked={formData?.permissions?.includes(permission?.id)}
                      onChange={() => handlePermissionToggle(permission?.id)}
                    />
                  ))}
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-border bg-muted/50">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
              >
                {labels?.cancel?.[language]}
              </Button>
              <Button
                type="submit"
                variant="default"
                iconName="Save"
                iconPosition="left"
              >
                {labels?.save?.[language]}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default UserFormModal;